import { useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_NEAR_GRADUATION_MAX_COURSES,
  MAX_NEAR_GRADUATION_MAX_COURSES,
} from '@dosfilos/domain';
import { tenantConfigApi } from '../../../lib/api/tenant-config.api';
import { errorText } from '../../../lib/error-text';
import { useTenantConfig } from '../../../hooks/use-tenant-config';

// ─── Candidatos a graduación: qué es «cerca» (M1 · PR A) ────────────────────
//
// «Cerca» = le faltan N cursos o menos para alguna credencial. Antes era un 4
// fijo en el código. Lo guarda `updateTenantConfig` (administración del
// seminario o plataforma), que lo valida y lo audita. Es PRESENTACIÓN: decide
// quién aparece en Reportes → Candidatos; no cambia el avance ni el
// expediente. Solo con el expediente por credenciales, como Candidatos.

export function CandidateNearEditor({ tenantId }: { tenantId: string }): JSX.Element | null {
  const { t } = useTranslation('sis');
  const queryClient = useQueryClient();
  const tenantConfig = useTenantConfig();
  const saved = tenantConfig.nearGraduationMaxCourses;
  const [draft, setDraft] = useState<number | ''>(saved);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => setDraft(saved), [saved]);

  if (tenantConfig.academicRecord.mode !== 'credentials') return null;
  const valid =
    typeof draft === 'number' &&
    Number.isInteger(draft) &&
    draft >= 1 &&
    draft <= MAX_NEAR_GRADUATION_MAX_COURSES;

  const save = async () => {
    if (!valid) return;
    setBusy(true);
    setMsg(null);
    try {
      await tenantConfigApi.updateTenantConfig({ tenantId, nearGraduationMaxCourses: draft });
      await queryClient.invalidateQueries({ queryKey: ['tenant-config-view', tenantId] });
      await queryClient.invalidateQueries({ queryKey: ['graduation-candidates', tenantId] });
      setMsg({ ok: true, text: t('settings.candidateNear.saved') });
    } catch (e) {
      setMsg({ ok: false, text: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700}>
        {t('settings.candidateNear.title')}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        {t('settings.candidateNear.desc', { default: DEFAULT_NEAR_GRADUATION_MAX_COURSES })}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
        <TextField
          size="small"
          type="number"
          label={t('settings.candidateNear.courses')}
          value={draft}
          onChange={(e) => setDraft(e.target.value === '' ? '' : Number(e.target.value))}
          inputProps={{ min: 1, max: MAX_NEAR_GRADUATION_MAX_COURSES }}
          error={!valid}
          helperText={
            valid
              ? t('settings.candidateNear.helper', { count: Number(draft) })
              : t('settings.candidateNear.invalid', { max: MAX_NEAR_GRADUATION_MAX_COURSES })
          }
          sx={{ maxWidth: 260 }}
        />
        <Button
          variant="outlined"
          onClick={() => void save()}
          disabled={busy || !valid || draft === saved}
        >
          {t('settings.candidateNear.save')}
        </Button>
      </Stack>
      {msg && (
        <Alert severity={msg.ok ? 'success' : 'error'} sx={{ mt: 1.5 }}>
          {msg.text}
        </Alert>
      )}
    </Box>
  );
}
